import { useEffect, useRef, useState } from "react"
import Icon from "@/v2-ui/icon"
import Dialog, { type DialogProps } from "@/v2-ui/dialog"
import { toast } from "@/v2-ui/toast"
import Button from "@/v2-backoffice-client/components/button"
import TranslationEditor from "@/v2-backoffice-client/translation/editor/TranslationEditor"
import TranslationAddRowFields from "@/v2-backoffice-client/translation/addRow/TranslationAddRowFields"
import TranslationAddRowLanguages from "@/v2-backoffice-client/translation/addRow/TranslationAddRowLanguages"
import TranslationAttributesField from "@/v2-backoffice-client/translation/attributes/TranslationAttributesField"
import {
  buildAttributesTemplate,
  EMPTY_ATTRIBUTES,
  parseAttributes,
  toAttributesDocument,
} from "@/v2-backoffice-client/translation/attributes/translation.attributes.utils"
import {
  blankEdit,
  type LangEdit,
} from "@/v2-backoffice-client/translation/editor/translation.editor.utils"
import { useTranslationAiFillMutation } from "@/v2-backoffice-client/translation/catalog/translation.aiFill.mutation"
import { useTranslationCreateFeatureMutation } from "@/v2-backoffice-client/translation/addRow/translation.createFeature.mutation"
import { useTranslationCreateTypeValueMutation } from "@/v2-backoffice-client/translation/addRow/translation.createTypeValue.mutation"
import { ENGLISH_LANGUAGE_CODE } from "@/v2-backoffice-client/translation/translation.constants"
import type {
  SupportedLanguage,
  TranslationBoard,
  TranslationCategory,
  TranslationRow,
} from "@/v2-backoffice-client/translation/translation.types"

// A fresh, empty per-language draft set. Used for the initial state and again
// by "Add another", which is the same starting point for the next code.
function blankEdits(languages: SupportedLanguage[]): Record<string, LangEdit> {
  return Object.fromEntries(
    languages.map((lang) => [lang.code, blankEdit("", "")]),
  )
}

export type TranslationAddRowDialogProps = DialogProps & {
  board: TranslationBoard
  // The catalog category in view (preselects the target), or null for the
  // unsliced catalog / Missing translations views.
  category: TranslationCategory | null
  // Full catalog refetch — a created row cannot be patched into the cache
  // (it's a whole new record), so the board reloads.
  onSaved(): void
}

// Every supported language is editable from the moment the dialog opens, in
// the exact row layout the catalog table's row-expansion uses
// (TranslationAddRowLanguages / TranslationEditorLanguage). The row doesn't
// exist in the DB yet, so "Translate from"/AI plumbing can't run against it
// — but Save and AI translate missing can, and both are overloaded: the
// first one to actually reach the server (once Code is filled in — no
// language text required, "AI translate missing" can draft everything from
// the code alone) transparently creates the row seeded with whatever
// languages already have text, then the view swaps to mounting the real
// TranslationEditor for that row — same component, same mutations, full
// "Translate from any language" from that point on. No separate "create"
// screen, no new AI plumbing.
function TranslationAddRowDialog(props: TranslationAddRowDialogProps) {
  const { board, category, onSaved, onOpenChange, ...dialogProps } = props

  const [createFeature, createFeatureState] =
    useTranslationCreateFeatureMutation()
  const [createTypeValue, createTypeValueState] =
    useTranslationCreateTypeValueMutation()
  const [aiFill, aiFillState] = useTranslationAiFillMutation()

  const [objectType, setObjectType] = useState<TranslationRow["objectType"]>(
    category?.section === "data" ? "TypeValue" : "Feature",
  )
  const [typeSet, setTypeSet] = useState(
    category?.section === "data" ? category.key : "",
  )
  // UI category keys are "UI_<Feature>": pre-typing the prefix nudges the
  // operator into the ADR-0025 UI_<Feature>_<Surface>_<Slot> shape.
  const [code, setCode] = useState(
    category?.section === "ui" ? `${category.key}_` : "",
  )
  const [edits, setEdits] = useState<Record<string, LangEdit>>(() =>
    blankEdits(board.languages),
  )
  // Raw JSON text, kept as typed so a half-written document survives a
  // re-render. Only sent for a TypeValue — a feature has no such column.
  const [attributesDraft, setAttributesDraft] = useState(() =>
    category?.section === "data"
      ? toAttributesDocument(buildAttributesTemplate(category.rows))
      : EMPTY_ATTRIBUTES,
  )
  // Saving also drafts the languages left empty. Default ON: without it, a row
  // saved with only en-GB goes straight into the Missing translations queue,
  // so the person who just wrote it is the one who has to come back for it.
  const [autoTranslate, setAutoTranslate] = useState(true)
  const [errorText, setErrorText] = useState<string | null>(null)
  // Set once the row is created — switches the dialog over to the real
  // per-row editor. board.rowsByCode won't have it the instant this is set
  // (the refetch triggered by onSaved is async), so there's a brief gap
  // where createdRowKey is set but createdRow is still null.
  const [createdRowKey, setCreatedRowKey] = useState<string | null>(null)
  const [finishingTimedOut, setFinishingTimedOut] = useState(false)
  // Synchronous re-entrancy guard for ensureRowCreated. Save-all and AI
  // translate missing both call it, and React state updates are not
  // synchronous — a `disabled`-driven guard alone leaves a gap where both
  // can start before either's state commits. A ref mutation has no such
  // gap. Deliberately left latched true through the success path (see
  // ensureRowCreated) — createdRowKey itself takes over as the guard once
  // its state update actually lands; only failure paths reset it, since
  // those are the only paths where a retry should be possible.
  const creatingRef = useRef(false)

  const creatingRow = createFeatureState.loading || createTypeValueState.loading
  const dataCategories = board.categories.filter(
    (candidate) => candidate.section === "data",
  )
  const createdRow = createdRowKey ? board.rowsByCode.get(createdRowKey) : null

  // The values already in the selected type set. Both the shape hint and the
  // starter document are derived from them, since nothing else describes what
  // keys a set's Attributes should carry — see buildAttributesTemplate.
  const siblingRows = typeSet
    ? (board.categoryByKey.get(typeSet)?.rows ?? [])
    : []
  const parsedAttributes = parseAttributes(attributesDraft)

  const filledLangCount = board.languages.filter(
    (lang) => (edits[lang.code]?.curName ?? "").trim() !== "",
  ).length
  const missingLangCount = board.languages.length - filledLangCount

  // Reseeded only where the target set actually changes (below), never from an
  // effect on `board`: the catalog refetches after every save, and reseeding on
  // that would wipe the document out from under a run of values. Which is also
  // why "Add another" leaves it alone — the next value of the same set almost
  // always wants the same keys, and usually the same values.
  function seedAttributes(nextTypeSet: string) {
    const rows = board.categoryByKey.get(nextTypeSet)?.rows ?? []
    setAttributesDraft(toAttributesDocument(buildAttributesTemplate(rows)))
  }

  // Bounds the createdRowKey-set/createdRow-not-found-yet gap above: if the
  // client-built row key ever fails to match what the server actually
  // stored it as, this stops the dialog from spinning forever.
  useEffect(() => {
    if (!createdRowKey || createdRow) return
    const timer = setTimeout(() => setFinishingTimedOut(true), 8000)
    return () => clearTimeout(timer)
  }, [createdRowKey, createdRow])

  function close() {
    onOpenChange?.(false)
  }

  function patch(langCode: string, next: Partial<LangEdit>) {
    setEdits((prev) =>
      prev[langCode]
        ? { ...prev, [langCode]: { ...prev[langCode], ...next } }
        : prev,
    )
  }

  // requireEnglish gates the manual save paths (the footer "Save all
  // changes" button and every per-language row's own "Save" button, which
  // both funnel here): a Feature row without an en-GB Name violates the
  // publish-time language invariant (publishTranslations.mjs) and 500s at
  // runtime, since feature reads scan Name non-nullable. "AI translate
  // missing" deliberately skips this check — it drafts every language still
  // empty, en-GB included, in the very same call, so the row is never left
  // incomplete by the time it lands.
  async function ensureRowCreated(
    opts: { requireEnglish?: boolean } = {},
  ): Promise<string | null> {
    if (createdRowKey) return createdRowKey
    if (creatingRef.current) return null
    creatingRef.current = true
    setErrorText(null)

    const trimmedCode = code.trim()
    const rowKey =
      objectType === "TypeValue" ? `${typeSet}|${trimmedCode}` : trimmedCode
    if (objectType === "TypeValue" && typeSet === "") {
      setErrorText("Select the type set the value belongs to.")
      creatingRef.current = false
      return null
    }
    if (trimmedCode === "") {
      setErrorText("Code must not be blank.")
      creatingRef.current = false
      return null
    }
    if (board.rowsByCode.has(rowKey)) {
      setErrorText(`${rowKey} already exists — edit the existing row instead.`)
      creatingRef.current = false
      return null
    }
    if (objectType === "TypeValue" && !parsedAttributes.ok) {
      setErrorText(`Attributes: ${parsedAttributes.error}`)
      creatingRef.current = false
      return null
    }
    if (
      opts.requireEnglish &&
      (edits[ENGLISH_LANGUAGE_CODE]?.curName ?? "").trim() === ""
    ) {
      setErrorText(
        'English (en-GB) is the anchor language — fill it in before saving, or use "AI translate missing" to draft it from the code.',
      )
      creatingRef.current = false
      return null
    }

    // Seed with every language that already has real text — not just
    // English — so anything the operator typed ahead is saved in one shot.
    const seedLanguages = board.languages
      .filter((lang) => (edits[lang.code]?.curName ?? "").trim() !== "")
      .map((lang) => ({
        LanguageCode: lang.code,
        Name: edits[lang.code].curName.trim(),
        Description: edits[lang.code].curDesc.trim() || null,
      }))

    try {
      let result: { Ok: boolean; Error?: string | null } | undefined
      if (objectType === "Feature") {
        const res = await createFeature({
          variables: { input: { Code: trimmedCode, Languages: seedLanguages } },
        })
        if (res.errors?.length) throw new Error(res.errors[0].message)
        result = res.data?.createFeature
      } else {
        const res = await createTypeValue({
          variables: {
            input: {
              TypeSet: typeSet,
              Code: trimmedCode,
              Languages: seedLanguages,
              // Sent on the create call, not left for a follow-up edit: a
              // value whose type table needs Level/CategoryCode and has none
              // breaks the next publish, and nothing in the catalog would have
              // told the operator that the row they just added is unshippable.
              Attributes: parsedAttributes.ok
                ? parsedAttributes.attributes
                : {},
            },
          },
        })
        if (res.errors?.length) throw new Error(res.errors[0].message)
        result = res.data?.createTypeValue
      }
      if (!result?.Ok) {
        throw new Error(result?.Error ?? "The row could not be created.")
      }

      toast({ title: `Added ${rowKey}.` })
      onSaved()
      setCreatedRowKey(rowKey)
      return rowKey
    } catch (error) {
      creatingRef.current = false
      toast({
        variant: "destructive",
        title: "Could not create the text",
        description: error instanceof Error ? error.message : String(error),
      })
      return null
    }
  }

  async function onDraftSave() {
    const rowKey = await ensureRowCreated({ requireEnglish: true })
    if (!rowKey) return
    if (autoTranslate && missingLangCount > 0) await runAiFillMissing(rowKey)
  }

  // Clears the draft for the NEXT code while keeping every choice that would
  // otherwise be re-made identically: Kind, Type set, the UI code prefix, the
  // attributes document, and the auto-translate preference. That is the whole
  // point — adding twelve values to one type set was twelve rounds of
  // close/reopen/re-pick before this.
  function onAddAnother() {
    setCreatedRowKey(null)
    setFinishingTimedOut(false)
    creatingRef.current = false
    setErrorText(null)
    setEdits(blankEdits(board.languages))
    setCode(category?.section === "ui" ? `${category.key}_` : "")
  }

  function onDraftSuggestFrom(lang: { label: string }) {
    toast({
      title: "Save a language with real text first.",
      description: `Once ${lang.label} (or any language) is saved, Translate from becomes available for every language.`,
    })
  }

  // Same contract as TranslationEditor's own "AI translate missing": create
  // the row if needed (seeded with whatever's already typed), then draft
  // AND save every language still empty at that moment in the same call.
  // Once this resolves, board.rowsByCode carries the fully-filled row and
  // the view swaps to the real TranslationEditor, already showing everything
  // written here.
  async function onDraftAiFillMissing() {
    const rowKey = await ensureRowCreated()
    if (!rowKey) return
    await runAiFillMissing(rowKey)
  }

  // Drafts and saves every language still empty on an already-created row.
  // Shared by the explicit "AI translate missing" button and the save path's
  // auto-translate, so both report the same way and neither can drift.
  async function runAiFillMissing(rowKey: string) {
    try {
      const res = await aiFill({
        variables: {
          input: {
            Items: [
              {
                Code: rowKey,
                ObjectType: objectType,
                Texts: board.languages
                  .filter(
                    (lang) => (edits[lang.code]?.curName ?? "").trim() !== "",
                  )
                  .map((lang) => ({
                    LanguageCode: lang.code,
                    Name: edits[lang.code].curName.trim(),
                    Description: edits[lang.code].curDesc.trim() || null,
                  })),
              },
            ],
          },
        },
      })
      if (res.errors?.length) throw new Error(res.errors[0].message)
      const result = res.data?.aiFillMissingTranslations?.[0]
      if (result?.Ok) {
        toast({
          title:
            result.FilledCount === 0
              ? "Nothing left to translate."
              : `Drafted and saved ${result.FilledCount} language(s).`,
        })
        onSaved()
      } else {
        toast({
          variant: "destructive",
          title: "AI translate failed",
          description: result?.Error ?? "unknown error",
        })
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "AI translate failed",
        description: error instanceof Error ? error.message : String(error),
      })
    }
  }

  // What ensureRowCreated itself requires — mirrored here so the AI-fill
  // button (which needs no language text, just a valid code) can enable
  // independently of whether anything's been typed.
  const canCreate =
    code.trim() !== "" &&
    (objectType !== "TypeValue" || (typeSet !== "" && parsedAttributes.ok))
  // Mirrors ensureRowCreated's requireEnglish check, for the manual-save
  // buttons' disabled state — see the block comment on ensureRowCreated.
  const hasEnglishName =
    (edits[ENGLISH_LANGUAGE_CODE]?.curName ?? "").trim() !== ""

  return (
    <Dialog onOpenChange={onOpenChange} {...dialogProps}>
      <Dialog.Content
        align="top"
        className="w-[800px] max-w-[95vw] max-h-[85vh] overflow-y-auto"
      >
        <Dialog.Header>
          <Dialog.Title>
            {createdRowKey ? `Translate ${createdRowKey}` : "Add entry"}
          </Dialog.Title>
          <Dialog.Description>
            {createdRowKey
              ? 'Added. Translate from any language that already has text, or write the rest by hand — every save below is immediate. "Add another" keeps this Kind and type set for the next code.'
              : "Fill in any languages you already know, or leave them all blank and let AI translate missing draft everything from the code. Saving or AI-translating creates the text immediately."}
          </Dialog.Description>
        </Dialog.Header>

        <div className="space-y-4">
          {createdRowKey === null && (
            <>
              <TranslationAddRowFields
                objectType={objectType}
                onObjectTypeChange={(next) => {
                  setObjectType(next)
                  if (next === "TypeValue") seedAttributes(typeSet)
                }}
                typeSet={typeSet}
                onTypeSetChange={(next) => {
                  setTypeSet(next)
                  seedAttributes(next)
                }}
                code={code}
                onCodeChange={setCode}
                dataCategories={dataCategories}
                disabled={creatingRow}
              />

              {objectType === "TypeValue" && (
                <TranslationAttributesField
                  value={attributesDraft}
                  onChange={setAttributesDraft}
                  siblingRows={siblingRows}
                  disabled={creatingRow}
                />
              )}
            </>
          )}

          {errorText && <p className="text-sm text-destructive">{errorText}</p>}

          {createdRow ? (
            <div className="translation-add-row-editor">
              <TranslationEditor
                row={createdRow}
                languages={board.languages}
                onSaved={onSaved}
                onSavedTexts={() => onSaved()}
                onDeleted={close}
              />
            </div>
          ) : createdRowKey ? (
            finishingTimedOut ? (
              <p className="py-8 text-sm text-muted-foreground">
                This is taking longer than expected. {createdRowKey} may already
                be saved — check All texts, or close this dialog and search for
                it.
              </p>
            ) : (
              <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
                <Icon loading size={16} aria-label="Loading" />
                Finishing up…
              </div>
            )
          ) : (
            <TranslationAddRowLanguages
              languages={board.languages}
              edits={edits}
              onPatch={patch}
              onSave={onDraftSave}
              onAiFillMissing={onDraftAiFillMissing}
              onSuggestFrom={onDraftSuggestFrom}
              filledLangCount={filledLangCount}
              missingLangCount={missingLangCount}
              canCreate={canCreate}
              hasEnglishName={hasEnglishName}
              creatingRow={creatingRow}
              aiFillLoading={aiFillState.loading}
              autoTranslate={autoTranslate}
              onAutoTranslateChange={setAutoTranslate}
              onCancel={close}
            />
          )}
        </div>

        {createdRowKey !== null && (
          <Dialog.Footer className="mt-6 flex justify-end gap-3">
            <Button
              variant="outline"
              iconSrc="plus-small"
              data-feature-code="UI_BackofficeTranslation_AddRow_AddAnother"
              onClick={onAddAnother}
              title={
                objectType === "TypeValue"
                  ? `Add another value to ${typeSet}, keeping the attributes above.`
                  : "Add another entry, keeping the Kind and code prefix above."
              }
            >
              Add Another
            </Button>
            <Button
              data-feature-code="UI_BackofficeTranslation_AddRow_Done"
              onClick={close}
            >
              Done
            </Button>
          </Dialog.Footer>
        )}
      </Dialog.Content>
    </Dialog>
  )
}

export default TranslationAddRowDialog
